import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Popup from "reactjs-popup";
import * as Yup from "yup";
import Loader from "../../../Snippets/Loader";
import { FaSpinner } from "react-icons/fa";
import Select from "react-select";
import { City, Country, State } from "country-state-city";
import axiosInstance from "../../../../Middleware/AxiosInstanceInterceptor";
import { FaCalendarAlt } from "react-icons/fa";

const IndividualAction = ({
  isPreviewOpen,
  onClose,
  userId,
  roles,
  operation,
  handleClose,
  handleSubmit,
  handleUpdate,
  formData,
  isLoadingUpdate,
  userType = "individual", // Default to individual, can be passed as prop
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateUserValue, setUpdatedUserValues] = useState([]);
  const [allChecked, setAllChecked] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const indiaOption = Country.getAllCountries().find(
    (country) => country.name === "India",
  );

  // Initialize selectedCountry with India
  useEffect(() => {
    if (indiaOption && !selectedCountry) {
      setSelectedCountry(indiaOption);
    }
  }, [indiaOption, selectedCountry]);

  const handleStateChange = (selectedState, setValues) => {
    if (selectedState) {
      setValues((prevValues) => ({
        ...prevValues,
        state_name: selectedState.name || "",
        city_name: "", // Reset city name when state changes
      }));
      setSelectedState(selectedState);
      setSelectedCity(null); // Reset city when state changes
    }
  };

  const handleCityChange = (selectedCity, setValues) => {
    if (selectedCity) {
      setValues((prevValues) => ({
        ...prevValues,
        city_name: selectedCity.name || "",
      }));
      setSelectedCity(selectedCity);
    }
  };

  // Validation schema for individual
  const individualValidationSchema = Yup.object({
    first_name: Yup.string()
      .required("First Name is Required")
      .min(2, "Must be at least 2 characters"),
    last_name: Yup.string()
      .required("Last Name is Required")
      .min(2, "Must be at least 2 characters"),
    email: Yup.string().required("Email is Required").email("Email is Invalid"),
    phone_number: Yup.string()
      .required("Mobile Number is Required")
      .matches(/^[0-9]{10}$/, "Mobile Number is Invalid"),
    whatsapp: Yup.string()
      .matches(/^[0-9]{10}$/, "WhatsApp Number is Invalid")
      .nullable(),
    gender: Yup.string().required("Gender is Required"),
    dob: Yup.date()
      .required("Date of Birth is Required")
      .max(new Date(), "Date of Birth cannot be in the future"),
    panNumber: Yup.string()
      .required("PAN is Required")
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format"),
    address: Yup.string().required("Correspondence Address is Required"),
    state_name: Yup.string().required("State is Required"),
    city_name: Yup.string().required("City is Required"),
  });

  // Validation schema for organization
  const organizationValidationSchema = Yup.object({
    name: Yup.string()
      .required("Organization Name is Required")
      .min(3, "Must be at least 3 characters"),
    email: Yup.string().required("Email is Required").email("Email is Invalid"),
    phone_number: Yup.string()
      .required("Mobile Number is Required")
      .matches(/^[0-9]{10}$/, "Mobile Number is Invalid"),
    whatsapp: Yup.string()
      .matches(/^[0-9]{10}$/, "WhatsApp Number is Invalid")
      .nullable(),
    panNumber: Yup.string()
      .required("PAN is Required")
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format"),
    doi: Yup.date()
      .required("Date of Incorporation is Required")
      .max(new Date(), "Date cannot be in the future"),
    bar_cid: Yup.string().required("Bar Council ID is Required"),
    organizationType: Yup.string().required("Organization Type is Required"),
    authorised_personal_name: Yup.string().required(
      "Authorized Person Name is Required",
    ),
    designation: Yup.string().required("Designation is Required"),
    address: Yup.string().required("Correspondence Address is Required"),
    state_name: Yup.string().required("State is Required"),
    city_name: Yup.string().required("City is Required"),
  });

  // Function to handle form submission
  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsLoading(true);
    try {
      const authToken =
        localStorage.getItem("authToken") ||
        document.cookie.replace(
          /(?:(?:^|.*;\s*)authToken\s*=\s*([^;]*).*$)|^.*$/,
          "$1",
        );

      // Determine module-id based on user type
      const moduleId =
        userType === "individual" || userType === "user" ? "user" : "entity";

      // Prepare headers with appropriate module-id
      const headers = {
        "Content-Type": "application/json",
        "module-id": moduleId,
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      };

      let requestBody;

      if (userType === "individual" || userType === "user") {
        // Prepare individual request body
        requestBody = {
          first_name: values.first_name,
          last_name: values.last_name,
          middle_name: values.middle_name || "",
          email: values.email,
          phone_number: values.phone_number || values.whatsapp,
          gender: values.gender,
          role: "user",
          name: `${values.first_name} ${values.last_name}`.trim(),
          address: {
            country: "IN", // Fixed as India
            state: selectedState?.isoCode || "",
            city: selectedCity?.name || "",
            pin_Code: values.pincode || 0,
            correspondence_address: values.address,
          },
          personal: {
            nationality: "Indian",
            userType: "party",
            pancard: values.panNumber,
            dob: new Date(values.dob).toISOString(),
          },
        };
      } else {
        // Prepare organization request body
        requestBody = {
          email: values.email,
          phone_number: values.phone_number || values.whatsapp,
          role: "entity",
          name: values.name,
          address: {
            country: "IN", // Fixed as India
            state: selectedState?.isoCode || "",
            city: selectedCity?.name || "",
            pin_Code: values.pincode || 0,
            correspondence_address: values.address,
          },
          personal: {
            nationality: "Indian",
            userType: "party",
            organizationType: values.organizationType,
            pancard: values.panNumber,
            doi: new Date(values.doi).toISOString(),
            bar_Council_Id: values.bar_cid,
            gst_number: values.gst_number || "",
            authorised_peronal_name: values.authorised_personal_name,
            designation: values.designation,
          },
        };
      }

      // Make API call
      const response = await axiosInstance.post("/user/create", requestBody, {
        headers,
      });

      if (response.status === 200 || response.status === 201) {
        alert(
          `${userType === "individual" || userType === "user" ? "Individual" : "Organization"} created successfully!`,
        );
        resetForm();
        handleClose();
        if (handleSubmit) {
          handleSubmit(response.data);
        }
      }
    } catch (error) {
      console.error("Error creating user:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";
      alert(
        `Failed to create ${userType === "individual" || userType === "user" ? "individual" : "organization"}. Error: ${errorMessage}`,
      );
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  // Get initial values based on user type
  const getInitialValues = () => {
    if (isEditing && updateUserValue) {
      return updateUserValue;
    }

    if (userType === "individual" || userType === "user") {
      return {
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        whatsapp: "",
        gender: "",
        dob: "",
        panNumber: "",
        address: "",
        state_name: "",
        city_name: "",
        pincode: "",
      };
    } else {
      return {
        name: "",
        email: "",
        phone_number: "",
        whatsapp: "",
        panNumber: "",
        doi: "",
        bar_cid: "",
        organizationType: "",
        gst_number: "",
        authorised_personal_name: "",
        designation: "",
        address: "",
        state_name: "",
        city_name: "",
        pincode: "",
      };
    }
  };

  // Get current validation schema based on user type
  const getValidationSchema = () => {
    if (userType === "individual" || userType === "user") {
      return individualValidationSchema;
    } else {
      return organizationValidationSchema;
    }
  };

  return (
    <>
      <Popup open={isPreviewOpen} modal closeOnDocumentClick={false}>
        {(close) => (
          <div className="fixed inset-0 bg-black/40 flex items-start md:items-center justify-center p-4 z-50">
            {isLoading ? (
              <Loader />
            ) : (
              <div className="w-full max-w-5xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="w-full max-w-5xl mx-auto">
                  <Formik
                    initialValues={getInitialValues()}
                    validationSchema={getValidationSchema()}
                    onSubmit={handleFormSubmit}
                    enableReinitialize
                  >
                    {(formikProps) => (
                      <Form>
                        <div className="px-6 sm:px-10 py-8">
                          <div className="flex justify-between items-center mb-4">
                            <button
                              type="button"
                              onClick={() => {
                                handleClose();
                                close();
                                setUpdatedUserValues(null);
                              }}
                              className="text-gray-500 hover:text-red-500 text-xl font-bold"
                            >
                              ✕
                            </button>
                          </div>
                          <h2 className="text-2xl font-semibold text-gray-800 border-b pb-4 mb-6">
                            {isEditing
                              ? `Update ${userType === "individual" || userType === "user" ? "Individual" : "Organization"}`
                              : `Add ${userType === "individual" || userType === "user" ? "Individual" : "Organization"}`}
                          </h2>

                          {userType === "individual" || userType === "user" ? (
                            // INDIVIDUAL FORM FIELDS
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="mb-4">
                                  <label
                                    htmlFor="first_name"
                                    className="label-style"
                                  >
                                    First Name
                                    <span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    placeholder="Enter First Name"
                                    id="first_name"
                                    name="first_name"
                                    className="input-style"
                                  />
                                  <ErrorMessage
                                    name="first_name"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="middle_name"
                                    className="label-style"
                                  >
                                    Middle Name
                                  </label>
                                  <Field
                                    type="text"
                                    id="middle_name"
                                    name="middle_name"
                                    placeholder="Enter middle name"
                                    className="input-style"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="last_name"
                                    className="label-style"
                                  >
                                    Last Name<span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    id="last_name"
                                    name="last_name"
                                    placeholder="Enter last name"
                                    className="input-style"
                                  />
                                  <ErrorMessage
                                    name="last_name"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              {/* PAN, Gender */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="mb-4">
                                  <label
                                    htmlFor="panNumber"
                                    className="label-style"
                                  >
                                    PAN<span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    placeholder="Enter PAN"
                                    id="panNumber"
                                    name="panNumber"
                                    className="input-style"
                                    maxLength="10"
                                    style={{ textTransform: "uppercase" }}
                                  />
                                  <ErrorMessage
                                    name="panNumber"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                                <div className="mb-4 w-full">
                                  <label className="label-style">
                                    Gender<span className="required">*</span>
                                  </label>

                                  <Select
                                    options={[
                                      { value: "male", label: "Male" },
                                      { value: "female", label: "Female" },
                                      { value: "other", label: "Other" },
                                    ]}
                                    placeholder="Select Gender"
                                    value={
                                      formikProps.values.gender
                                        ? {
                                            value: formikProps.values.gender,
                                            label:
                                              formikProps.values.gender
                                                .charAt(0)
                                                .toUpperCase() +
                                              formikProps.values.gender.slice(
                                                1,
                                              ),
                                          }
                                        : null
                                    }
                                    onChange={(option) =>
                                      formikProps.setFieldValue(
                                        "gender",
                                        option.value,
                                      )
                                    }
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                      }),
                                    }}
                                  />

                                  <ErrorMessage
                                    name="gender"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              {/* Email, Phone */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                  <label
                                    htmlFor="email"
                                    className="label-style"
                                  >
                                    Email ID<span className="required">*</span>
                                  </label>
                                  <Field
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="Enter email Id"
                                    className="input-style"
                                  />
                                  <ErrorMessage
                                    name="email"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="phone_number"
                                    className="label-style"
                                  >
                                    Phone Number
                                    <span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    id="phone_number"
                                    name="phone_number"
                                    placeholder="Enter phone number"
                                    className="input-style"
                                    maxLength="10"
                                  />
                                  <ErrorMessage
                                    name="phone_number"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              {/* Whatsapp, DOB */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                  <label
                                    htmlFor="whatsapp"
                                    className="label-style"
                                  >
                                    WhatsApp Number
                                  </label>
                                  <Field
                                    type="text"
                                    id="whatsapp"
                                    name="whatsapp"
                                    placeholder="Enter WhatsApp number"
                                    className="input-style"
                                    maxLength="10"
                                  />
                                  <ErrorMessage
                                    name="whatsapp"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

<div className="mb-4 cursor-pointer">
  <label className="label-style">
    Date of Birth<span className="required">*</span>
  </label>

  <div className="relative w-full">

    <DatePicker
      selected={
        formikProps.values.dob
          ? new Date(formikProps.values.dob)
          : null
      }
      onChange={(date) => formikProps.setFieldValue("dob", date)}
      dateFormat="dd/MM/yyyy"
      maxDate={new Date()}
      placeholderText="Select Date of Birth"
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
      wrapperClassName="w-full"
      className="input-style w-full pr-12"
    />

    {/* Calendar Icon */}
    <FaCalendarAlt
      className="absolute right-3 top-[15px] text-gray-400 pointer-events-none cursor-pointer"
      size={18}
    />

  </div>

  <ErrorMessage
    name="dob"
    component="div"
    className="error text-red-600 text-sm mt-1"
  />
</div>
                              </div>
                            </>
                          ) : (
                            // ORGANIZATION FORM FIELDS
                            <>
                              <div className="mb-4">
                                <label htmlFor="name" className="label-style">
                                  Organization Name
                                  <span className="required">*</span>
                                </label>
                                <Field
                                  type="text"
                                  id="name"
                                  name="name"
                                  placeholder="Enter organization name"
                                  className="input-style w-full"
                                />
                                <ErrorMessage
                                  name="name"
                                  component="div"
                                  className="error text-red-600 text-sm mt-1"
                                />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                  <label
                                    htmlFor="panNumber"
                                    className="label-style"
                                  >
                                    PAN<span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    placeholder="Enter PAN"
                                    id="panNumber"
                                    name="panNumber"
                                    className="input-style"
                                    maxLength="10"
                                    style={{ textTransform: "uppercase" }}
                                  />
                                  <ErrorMessage
                                    name="panNumber"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="organizationType"
                                    className="label-style"
                                  >
                                    Organization Type
                                    <span className="required">*</span>
                                  </label>
                                  <select
                                    id="organizationType"
                                    name="organizationType"
                                    className="input-style"
                                    value={formikProps.values.organizationType}
                                    onChange={formikProps.handleChange}
                                  >
                                    <option value="">Select</option>
                                    <option value="government">
                                      Government
                                    </option>
                                    <option value="private">Private</option>
                                    <option value="public">Public</option>
                                    <option value="ngo">NGO</option>
                                    <option value="other">Other</option>
                                  </select>
                                  <ErrorMessage
                                    name="organizationType"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                  <label
                                    htmlFor="email"
                                    className="label-style"
                                  >
                                    Email ID<span className="required">*</span>
                                  </label>
                                  <Field
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="Enter organization email"
                                    className="input-style"
                                  />
                                  <ErrorMessage
                                    name="email"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="phone_number"
                                    className="label-style"
                                  >
                                    Phone Number
                                    <span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    id="phone_number"
                                    name="phone_number"
                                    placeholder="Enter phone number"
                                    className="input-style"
                                    maxLength="10"
                                  />
                                  <ErrorMessage
                                    name="phone_number"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                  <label
                                    htmlFor="whatsapp"
                                    className="label-style"
                                  >
                                    WhatsApp Number
                                  </label>
                                  <Field
                                    type="text"
                                    id="whatsapp"
                                    name="whatsapp"
                                    placeholder="Enter WhatsApp number"
                                    className="input-style"
                                    maxLength="10"
                                  />
                                  <ErrorMessage
                                    name="whatsapp"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="bar_cid"
                                    className="label-style"
                                  >
                                    Bar Council ID
                                    <span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    id="bar_cid"
                                    name="bar_cid"
                                    placeholder="Enter Bar Council ID"
                                    className="input-style"
                                  />
                                  <ErrorMessage
                                    name="bar_cid"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                  <label htmlFor="doi" className="label-style">
                                    Date of Incorporation
                                    <span className="required">*</span>
                                  </label>
                                  <Field
                                    type="date"
                                    id="doi"
                                    name="doi"
                                    className="input-style"
                                    max={new Date().toISOString().split("T")[0]}
                                  />
                                  <ErrorMessage
                                    name="doi"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="gst_number"
                                    className="label-style"
                                  >
                                    GST Number
                                  </label>
                                  <Field
                                    type="text"
                                    id="gst_number"
                                    name="gst_number"
                                    placeholder="Enter GST number"
                                    className="input-style"
                                  />
                                </div>
                              </div>

                              <div className="field-split">
                                <div className="mb-4">
                                  <label
                                    htmlFor="authorised_personal_name"
                                    className="label-style"
                                  >
                                    Authorized Person Name
                                    <span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    id="authorised_personal_name"
                                    name="authorised_personal_name"
                                    placeholder="Enter authorized person name"
                                    className="input-style"
                                  />
                                  <ErrorMessage
                                    name="authorised_personal_name"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>

                                <div className="mb-4">
                                  <label
                                    htmlFor="designation"
                                    className="label-style"
                                  >
                                    Designation
                                    <span className="required">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    id="designation"
                                    name="designation"
                                    placeholder="Enter designation"
                                    className="input-style"
                                  />
                                  <ErrorMessage
                                    name="designation"
                                    component="div"
                                    className="error text-red-600 text-sm mt-1"
                                  />
                                </div>
                              </div>
                            </>
                          )}

                          {/* COMMON FIELDS (Address Section) */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                              <div className="mb-4">
                                <label
                                  htmlFor="state_name"
                                  className="label-style"
                                >
                                  State<span className="required">*</span>
                                </label>
                                <Select
                                  name="state_name"
                                  id="state_name"
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: state.isFocused
                                        ? "#3B82F6"
                                        : "#e2e8f0",
                                      border: "2px solid #e2e8f0",
                                      padding: "4px",
                                      marginTop: "1px",
                                    }),
                                  }}
                                  options={State?.getStatesOfCountry("IN")}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.name}
                                  value={selectedState}
                                  onChange={(selectedOption) =>
                                    handleStateChange(
                                      selectedOption,
                                      formikProps.setValues,
                                    )
                                  }
                                />
                                <ErrorMessage
                                  name="state_name"
                                  component="div"
                                  className="error text-red-600 text-sm mt-1"
                                />
                              </div>

                              <div className="mb-4">
                                <label
                                  htmlFor="city_name"
                                  className="label-style"
                                >
                                  City<span className="required">*</span>
                                </label>
                                <Select
                                  name="city_name"
                                  id="city_name"
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: state.isFocused
                                        ? "#3B82F6"
                                        : "#e2e8f0",
                                      border: "2px solid #e2e8f0",
                                      padding: "4px",
                                      marginTop: "1px",
                                    }),
                                  }}
                                  options={City.getCitiesOfState(
                                    selectedState?.countryCode,
                                    selectedState?.isoCode,
                                  )}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.name}
                                  value={selectedCity}
                                  onChange={(selectedOption) =>
                                    handleCityChange(
                                      selectedOption,
                                      formikProps.setValues,
                                    )
                                  }
                                  isDisabled={!selectedState}
                                />
                                <ErrorMessage
                                  name="city_name"
                                  component="div"
                                  className="error text-red-600 text-sm mt-1"
                                />
                              </div>

                              <div className="mb-4">
                                <label
                                  htmlFor="pincode"
                                  className="label-style"
                                >
                                  Pincode
                                </label>
                                <Field
                                  type="text"
                                  id="pincode"
                                  name="pincode"
                                  placeholder="Enter pincode"
                                  className="input-style"
                                  maxLength="6"
                                />
                              </div>
                            </div>

                            <div className="mb-4">
                              <label htmlFor="address" className="label-style">
                                Correspondence Address
                                <span className="required">*</span>
                              </label>
                              <Field
                                as="textarea"
                                placeholder="Enter correspondence address"
                                id="address"
                                name="address"
                                className="input-style w-full min-h-[120px]"
                                rows="4"
                              />
                              <ErrorMessage
                                name="address"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>
                          </div>

                          <div className="flex justify-center mt-10">
                            <button
                              type="submit"
                              className="btn-style2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                              disabled={isLoading || formikProps.isSubmitting}
                            >
                              {isLoading || formikProps.isSubmitting ? (
                                <FaSpinner className="animate-spin mx-auto" />
                              ) : (
                                `Add ${userType === "individual" || userType === "user" ? "Individual" : "Organization"}`
                              )}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            )}
          </div>
        )}
      </Popup>
    </>
  );
};

export default IndividualAction;
